import React from 'react';
import {
    FormProvider,
    UseFormClearErrors,
    UseFormRegister,
    UseFormSetValue,
    useForm,
} from 'react-hook-form';
import {
    BoundedContent,
    Box,
    BoxProps,
    Column,
    FlexBox,
    FormField,
    FormLabel,
    GridContainer,
    Row,
    responsive,
    tokensRaw,
} from '@vp/swan';
import { TextAreaField } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { NormalisedBriefProduct } from '../BriefContext';
import { BriefFormProvider } from '../BriefContext/BriefFormContext';
import { Input as ProductDropdown } from '../BriefForm/FormFields/ClientConfiguredFields/ProductDropdown/ProductDropdown';
import { HowItWorks } from '../HowItWorks/HowItWorks';
import { Brief } from './BriefForm';
import { IntelligentBriefFAQ } from './IntelligentBriefFAQ';
import { IntroHeader } from './IntroHeader';
import { PromptInputField, PromptInputProps } from './PromptInputField';
import { StepProductCard } from './StepProductCard';
import { GenerateMutationFn } from './brief.generated';
import type { InvitedDesigner } from './InvitedDesigner';

const ResponsiveBox = responsive(Box);

function SectionWrapper(props: BoxProps) {
    const { children, ...restProps } = props;
    return (
        <ResponsiveBox style={{ width: '100%' }} xs={{ paddingX: '5' }} {...restProps}>
            <GridContainer>
                <Row>
                    <Column
                        span={12}
                        spanMd={10}
                        spanLg={8}
                        spanXl={8}
                        style={{ maxWidth: '1000px', minWidth: '343px', margin: '0 auto' }}
                        paddingX={0}
                    >
                        {children}
                    </Column>
                </Row>
            </GridContainer>
        </ResponsiveBox>
    );
}

interface IntelligentBriefProps {
    briefId: string;
    product?: NormalisedBriefProduct;
    generate: GenerateMutationFn;
    brief: Brief;
}
export const IntelligentBriefForm: React.FC<IntelligentBriefProps> = ({
    briefId,
    product,
    generate,
    brief,
}: IntelligentBriefProps) => {
    const methods = useForm<{ requestType: string }>();

    // project overview value saved to brief to be used for prefilling prompt input when user goes back
    const projectOverviewValue = (
        brief.dynamicFields.find((field) => field.id === 'projectOverview') as TextAreaField
    )?.textAreaValue;
    const invitedDesigner = brief.request?.invitedDesigners?.[0];

    return (
        <BoundedContent pl={0} pr={0}>
            <BriefFormProvider briefId={briefId}>
                <FormProvider {...methods}>
                    <FlexBox
                        justifyContent={'center'}
                        pb={6}
                        pt={8}
                        style={{ backgroundColor: `${tokensRaw.SwanSemColorBgInfo}` }}
                    >
                        <PromptInputSection
                            briefId={briefId}
                            clientConfiguredValue={projectOverviewValue ?? ''}
                            generate={generate}
                        />
                    </FlexBox>
                    <FlexBox flexDirection="column" alignItems="center" marginBottom={7}>
                        <ProductSelection
                            clearErrors={methods.clearErrors}
                            register={methods.register}
                            setValue={methods.setValue}
                            selectedProduct={product || undefined}
                        />
                        <HowItWorksContainer />
                        {product && (
                            <ProductCard product={product} invitedDesigner={invitedDesigner} />
                        )}
                    </FlexBox>
                    <FAQSection />
                </FormProvider>
            </BriefFormProvider>
        </BoundedContent>
    );
};

function PromptInputSection({ briefId, generate, clientConfiguredValue }: PromptInputProps) {
    return (
        <SectionWrapper>
            <IntroHeader />
            <PromptInputField
                briefId={briefId}
                generate={generate}
                clientConfiguredValue={clientConfiguredValue}
            />
        </SectionWrapper>
    );
}

function ProductSelection({
    selectedProduct,
    register,
    setValue,
    clearErrors,
}: {
    selectedProduct: NormalisedBriefProduct | undefined;
    register: UseFormRegister<any>;
    setValue: UseFormSetValue<any>;
    clearErrors: UseFormClearErrors<any>;
}) {
    // Note: "display" as null means this will always be visible as opposed to
    //       dynamically being shown based on the configuration.

    return (
        <SectionWrapper mt={7}>
            <FormField style={{ width: '100%' }}>
                <FormLabel
                    id={'physicalProduct'}
                    fontSkin="body-standard-bold"
                    style={{ display: 'block', paddingBottom: '4px' }}
                >
                    {__('Your selected product', {
                        _context: 'This is the label for the product selection dropdown',
                    })}
                </FormLabel>
                <ProductDropdown
                    __typename="ClientConfiguredField"
                    questionId="physicalProduct"
                    groupId="chooseYourPhysicalProduct"
                    id="physicalProduct"
                    clientConfiguredValue={
                        selectedProduct?.fulfilmentMpvId?.toLocaleLowerCase() || ''
                    }
                    display={null}
                    required={true}
                    immutable={false}
                    label="Choose a product for us to design"
                    register={register}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    errorMessage={__('Please select a product type', {
                        _context: 'This is the error message for product selection',
                    })}
                />
            </FormField>
        </SectionWrapper>
    );
}

function HowItWorksContainer() {
    return (
        <SectionWrapper mt={5}>
            <HowItWorks
                isIntelligentBrief={true}
                textAlign="center"
                mdStepsDirection="row"
                mdStepItemDirection="column"
            />
        </SectionWrapper>
    );
}

function ProductCard({
    product,
    invitedDesigner,
}: {
    product: NormalisedBriefProduct;
    invitedDesigner?: InvitedDesigner | null;
}) {
    return (
        <SectionWrapper mt={7} mb={7}>
            <StepProductCard product={product} invitedDesigner={invitedDesigner} />
        </SectionWrapper>
    );
}

function FAQSection() {
    return (
        <SectionWrapper backgroundColor={'warm-white'}>
            <IntelligentBriefFAQ />
        </SectionWrapper>
    );
}
